<template>
  <b-container class="mt-5">
    <b-row class="justify-content-center">
      <b-col cols="12" md="8" lg="6">
        <b-button
          v-if="showTable"
          variant="primary"
          class="mb-3"
          block
          @click="back"
        >
          Back
        </b-button>
      </b-col>
    </b-row>

    <b-row class="justify-content-center">
      <b-col cols="12" md="8" lg="6" v-if="!showTable">
        <b-card header="Select Date Range" header-tag="h4" class="shadow">
          <b-form>
            <!-- Date From -->
            <b-form-group label="Date From" label-for="date-from">
              <b-form-datepicker
                id="date-from"
                v-model="dateFrom"
                placeholder="Select start date"
                :max="dateTo"
              ></b-form-datepicker>
            </b-form-group>

            <!-- Date To -->
            <b-form-group label="Date To" label-for="date-to">
              <b-form-datepicker
                id="date-to"
                v-model="dateTo"
                placeholder="Select end date"
                :min="dateFrom"
              ></b-form-datepicker>
            </b-form-group>

            <!-- Submit Button -->
            <b-button variant="primary" class="mt-3" block @click="submitDates">
              Submit
            </b-button>
          </b-form>
        </b-card>
      </b-col>

      <b-col cols="12" v-if="showTable">
        <b-card :header="tdsHeader" header-tag="h4" class="shadow">
          <!-- Export to CSV Button -->
          <b-button variant="success" class="mb-3" @click="exportToCSV">
            Export to CSV
          </b-button>

          <!-- Table -->
          <b-table
            striped
            hover
            responsive="sm"
            :items="formattedTableData"
            :fields="tableFields"
          >
            :sort-by.sync="sortBy" :sort-desc.sync="sortDesc"
            @sort-changed="onSortChanged">
          </b-table>

          <b-row class="w-100">
              <b-col cols="6" class="mt-2">
                <h5 class="text-uppercase">Total</h5>
              </b-col>

              <b-col cols="6" class="mt-xl-0 mt-2 d-flex justify-content-end">
                <div class="font-weight-bolder justify-content-end text-right">
                  <hr class="my-50" />
                  <p class="card-text mb-25">Debit: {{ getTotalDebit | formatCurrency }}</p>
                  <p class="card-text mb-25">Credit: {{ getTotalCredit | formatCurrency }}</p>
                </div>
              </b-col>
            </b-row>
        </b-card>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import Swal from "sweetalert2";
import {
  BContainer,
  BRow,
  BCol,
  BCard,
  BForm,
  BFormGroup,
  BFormDatepicker,
  BButton,
  BTable,
} from "bootstrap-vue";
import { mapActions } from "vuex";
import { format } from "date-fns";
export default {
  components: {
    BContainer,
    BRow,
    BCol,
    BCard,
    BForm,
    BFormGroup,
    BFormDatepicker,
    BButton,
    BTable,
  },
  data() {
    return {
      dateFrom: null,
      dateTo: null,
      showTable: false,
      tableData: [],
      sortBy: "date", // Default sort field
      sortDesc: false, // Default sort direction (ascending)
      tableFields: [
        { key: "category", label: "Category", sortable: true },
        { key: "subCategory", label: "Sub Category", sortable: true },
        { key: "description", label: "Description", sortable: true },
        { key: "debit", label: "Debit", sortable: true },
        { key: "credit", label: "Credit", sortable: true },
        { key: "date", label: "Date", sortable: true },
      ],
    };
  },
  computed: {
    getTotalDebit() {
      return this.tableData ?
      this.tableData
          .reduce((total, row) => {
            const debit = parseFloat(row.debit);
            return !isNaN(debit) ? total + debit : total;
          }, 0)
          .toFixed(2)
        : 0;
    },
    getTotalCredit() {
      return this.tableData ?
      this.tableData
          .reduce((total, row) => {
            const credit = parseFloat(row.credit);
            return !isNaN(credit) ? total + credit : total;
          }, 0)
          .toFixed(2)
        : 0;
    },
    tdsHeader() {
      return this.dateFrom && this.dateTo
        ? `General Ledger Data from: ${this.dateFrom} to: ${this.dateTo}`
        : "General Ledger Data";
    },
    formattedTableData() {
      return this.tableData.map((item) => ({
        category: item.category ?? "-",
        subCategory: item.subCategory ?? "-",
        description: item.description ?? "-",
        debit: item.debit ?? "-",
        date: item.date ? format(new Date(item.date), "dd-MM-yyyy") : "-",
        credit: item.credit ?? "-",
      }));
    },
  },
  methods: {
    ...mapActions("reportModule", ["getGeneralLedgerReportAction"]),

    async submitDates() {
      if (!this.dateFrom || !this.dateTo) {
        this.$bvToast.toast("Please select both dates.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
        return;
      }

      try {
        Swal.fire({
          title: "Fetching General Ledger data...",
          text: "Please wait...",
          didOpen: () => {
            Swal.showLoading();
          },
          allowOutsideClick: false,
        });

        const payload = {
          dateFrom: this.dateFrom
            ? this.dateFrom
            : new Date().toISOString().split("T")[0], // Default to today's date if missing
          dateTo: this.dateTo
            ? this.dateTo
            : new Date().toISOString().split("T")[0],
        };

        console.log("API Request Payload:", payload);

        const response = await this.getGeneralLedgerReportAction(payload);
        Swal.close();
        console.log("API Response:", response);

        if (response && response.length > 0) {
          this.tableData = response;
          this.showTable = true;
        } else {
          Swal.fire({
            title: "No Data",
            text: "No data available for the selected period.",
            icon: "warning",
            confirmButtonText: "OK",
          });
        }
      } catch (error) {
        console.error("Error fetching TDS data:", error);

        if (
          error.response &&
          error.response.data &&
          error.response.data.errors
        ) {
          const errors = error.response.data.errors;
          let errorMessage = "Validation errors occurred:\n";

          for (const key in errors) {
            if (errors.hasOwnProperty(key)) {
              errorMessage += `${key}: ${errors[key].join(", ")}\n`;
            }
          }

          Swal.fire({
            title: "Validation Error",
            text: errorMessage,
            icon: "error",
            confirmButtonText: "OK",
          });
        } else {
          Swal.fire({
            title: "Error",
            text: "Failed to fetch General Ledger data. Please try again.",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    },

    formatCurrency(value) {
      return new Intl.NumberFormat("en-MU", {
        style: "currency",
        currency: "MUR",
      }).format(value);
    },
    back() {
      this.showTable = false;
    },

    exportToCSV() {
      // Generate CSV headers from table fields
      const headers = this.tableFields.map((field) => field.label).join(",");

      // Generate CSV rows from the formattedTableData
      const rows = this.formattedTableData.map((item) =>
        this.tableFields.map((field) => `"${item[field.key] ?? "-"}"`).join(",")
      );

      // Combine headers and rows into a single CSV string
      const csvContent = [headers, ...rows].join("\n");

      // Create a Blob for the CSV content
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

      // Create a temporary download link
      const link = document.createElement("a");
      const currentDate = new Date().toISOString().slice(0, 10); // Format: YYYY-MM-DD
      link.href = URL.createObjectURL(blob);
      link.download = `Table_Data_${currentDate}.csv`;

      // Trigger the download
      link.click();

      // Revoke the Blob URL to free up memory
      URL.revokeObjectURL(link.href);
    },
  },
  back() {
    this.showTable = false;
  },
};
</script>

<style scoped>
.b-card {
  border-radius: 10px;
  box-shadow: 0px 0px 15px rgba(0, 0, 0, 0.1);
}
</style>
